import { useState, useEffect } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "@/contexts/TradeAuthContext";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { 
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue 
} from "@/components/ui/select";
import { 
  Plus, UserCog, Trash2, ArrowLeft, Save, Star,
  Phone, Mail, MapPin, MessageCircle, Globe, Truck,
  Megaphone, MessageSquare, X, Copy, Square, CheckSquare, Paperclip
} from "lucide-react";
import SendDocumentModal from "@/pages/trade/SendDocumentModal";
import CallPrompt from "@/pages/trade/CallPrompt";
import ContactAudioRecordButton from "@/pages/trade/ContactAudioRecordButton";
import RecordedCallButton from "@/pages/trade/RecordedCallButton";
import { toast } from "sonner";

// Reference directory for the China → Djibouti → Ethiopia route (public contact info)
const DJIBOUTI_REFERENCE = [
  {
    key: "kothari",
    name: "J.J. Kothari & Co. Ltd.",
    services: "Port clearance, freight forwarding and heavy equipment logistics",
    phone: "+253 21 35 02 19",
    email: "nalin@kothari.dj",
    website: "https://www.kothari.dj",
  },
  {
    key: "mti",
    name: "Maritime Transport International (MTI)",
    services: "Freight forwarding and transit support",
    phone: "+253 21 35 14 55",
    email: "",
    website: "",
  },
  {
    key: "massida",
    name: "Massida Logistics",
    services: "Freight forwarding, customs clearance and heavy cargo",
    phone: "+253 21 35 15 31",
    email: "",
    website: "",
  },
  {
    key: "gtts",
    name: "Global Transit and Transport Services",
    services: "Djibouti-to-Ethiopia customs and transport coordination",
    phone: "+253 77 82 18 32",
    email: "info@gttsgroup.com",
    website: "https://www.gttsgroup.com",
  },
];

export const AgentsList = () => {
  const { API, authHeader } = useAuth();
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  const fetchData = () => {
    axios.get(`${API}/trade/agents`, { headers: authHeader() })
      .then(res => setAgents(res.data))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchData(); }, []);

  const handleDelete = async (id, e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!window.confirm("Delete this agent?")) return;
    await axios.delete(`${API}/trade/agents/${id}`, { headers: authHeader() });
    toast.success("Agent deleted");
    fetchData();
  };

  const filtered = agents.filter(a => filter === "all" || a.role === filter);

  // Bulk messaging state + helpers
  const [selected, setSelected] = useState([]);
  const [composeOpen, setComposeOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [copied, setCopied] = useState(false);
  const [attachAgent, setAttachAgent] = useState(null);

  const toggleSelect = (id) => {
    setSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };
  const allSelected = filtered.length > 0 && filtered.every(a => selected.includes(a.id));
  const toggleSelectAll = () => {
    setSelected(allSelected ? [] : filtered.map(a => a.id));
  };
  const selectedAgents = agents.filter(a => selected.includes(a.id));
  const selectedPhones = selectedAgents.map(a => a.phone).filter(Boolean);
  const waNumber = (num) => (num || "").replace(/[^0-9]/g, "");

  // Copy a reference company into the personal agent list (Djibouti → Ethiopia role)
  const saveReference = async (co) => {
    if (agents.some(a => a.name === co.name)) {
      toast.info(`${co.name} is already in your agents`);
      return;
    }
    try {
      await axios.post(`${API}/trade/agents`, {
        name: co.name,
        role: "djibouti_transporter",
        company: co.name,
        phone: co.phone,
        email: co.email,
        country: "Djibouti",
        services: co.services,
        notes: co.website ? `Website: ${co.website}` : "",
      }, { headers: authHeader() });
      toast.success(`${co.name} saved to your agents`);
      fetchData();
    } catch {
      toast.error("Failed to save");
    }
  };
  const copyMessage = async () => {
    try {
      await navigator.clipboard.writeText(message);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {}
  };

  const ROLE_LABELS = {
    transit: "China Transit Agent",
    shipping: "Shipping Line Agent",
    djibouti_transporter: "Djibouti → Ethiopia Transporter",
    ethiopia_transiter: "Ethiopia Transiter",
  };

  const roleTabs = [
    { value: "all", label: "All", count: agents.length },
    { value: "transit", label: "China Transit", count: agents.filter(a => a.role === "transit").length },
    { value: "shipping", label: "Shipping Line", count: agents.filter(a => a.role === "shipping").length },
    { value: "djibouti_transporter", label: "Djibouti → Ethiopia", count: agents.filter(a => a.role === "djibouti_transporter").length },
    { value: "ethiopia_transiter", label: "Ethiopia Transiter", count: agents.filter(a => a.role === "ethiopia_transiter").length },
  ];

  return (
    <div className="trade-section pt-32 lg:pt-20 pb-20 min-h-screen">
      <div className="lg:ml-64 px-6 md:px-12 py-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-start justify-between flex-wrap gap-4 mb-10"
        >
          <div>
            <p className="text-xs uppercase tracking-[0.3em] trade-gold mb-3">Agents</p>
            <h1 className="text-4xl md:text-5xl font-serif font-bold">Transit & Shipping Agents</h1>
            <p className="text-white/50 mt-2">Coordinators connecting suppliers with shipping lines</p>
          </div>
          <Link to="/trade/agents/new">
            <Button className="trade-btn-primary px-6 py-6 rounded-sm" data-testid="new-agent-btn">
              <Plus size={18} className="mr-2" /> New Agent
            </Button>
          </Link>
        </motion.div>

        {/* Djibouti route reference directory */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="trade-card rounded-sm p-6 mb-8"
          data-testid="djibouti-reference"
        >
          <h3 className="text-sm uppercase tracking-widest trade-gold mb-1 flex items-center gap-2">
            <Truck size={14} /> Djibouti Transit Companies You Can Contact
          </h3>
          <p className="text-[11px] text-white/40 mb-5">
            Reference directory for the China → Djibouti → Ethiopia route
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {DJIBOUTI_REFERENCE.map(co => (
              <div key={co.key} className="bg-white/5 rounded-sm p-4" data-testid={`ref-${co.key}`}>
                <h4 className="font-serif font-semibold text-sm">{co.name}</h4>
                <p className="text-[11px] text-white/50 mt-1 mb-3">{co.services}</p>
                <div className="flex flex-wrap items-center gap-2">
                  <CallPrompt
                    phone={co.phone.replace(/\s/g, "")}
                    name={co.name}
                    buttonLabel={co.phone}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-[11px] rounded-sm bg-white/5 text-white/70 hover:bg-white/10 transition-colors"
                    testid={`ref-call-${co.key}`}
                  />
                  <ContactAudioRecordButton
                    phone={co.phone.replace(/\s/g, "")}
                    name={co.name}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-[11px] rounded-sm bg-[#D4AF37]/10 trade-gold hover:bg-[#D4AF37]/20 transition-colors"
                    testid={`ref-audio-${co.key}`}
                  />
                  <RecordedCallButton
                    phone={co.phone.replace(/\s/g, "")}
                    name={co.name}
                    contactId={co.key}
                    contactType="agent_reference"
                    className="flex items-center gap-1.5 px-3 py-1.5 text-[11px] rounded-sm bg-red-500/10 text-red-300 hover:bg-red-500/20 transition-colors"
                    testid={`ref-record-call-${co.key}`}
                  />
                  {co.email && (
                    <a
                      href={`mailto:${co.email}`}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-[11px] rounded-sm bg-[#D4AF37]/10 trade-gold hover:bg-[#D4AF37]/20 transition-colors"
                      data-testid={`ref-email-${co.key}`}
                    >
                      <Mail size={11} /> Email
                    </a>
                  )}
                  {co.website && (
                    <a
                      href={co.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1.5 px-3 py-1.5 text-[11px] rounded-sm bg-[#10b981]/10 trade-green hover:bg-[#10b981]/20 transition-colors"
                      data-testid={`ref-website-${co.key}`}
                    >
                      <Globe size={11} /> Website
                    </a>
                  )}
                  <button
                    onClick={() => saveReference(co)}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-[11px] rounded-sm border border-[#D4AF37]/30 trade-gold hover:bg-[#D4AF37]/10 transition-colors ml-auto"
                    data-testid={`ref-save-${co.key}`}
                  >
                    <Plus size={11} /> Save to My Agents
                  </button>
                </div>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Filter tabs */}
        <div className="flex gap-2 mb-6 overflow-x-auto">
          {roleTabs.map(t => (
            <button
              key={t.value}
              onClick={() => setFilter(t.value)}
              className={`px-4 py-2 text-xs rounded-sm whitespace-nowrap transition-all ${
                filter === t.value 
                  ? "bg-[#D4AF37] text-black font-semibold"
                  : "bg-white/5 text-white/60 hover:text-white"
              }`}
              data-testid={`filter-${t.value}`}
            >
              {t.label} ({t.count})
            </button>
          ))}
        </div>

        {/* Bulk messaging bar */}
        <div className="trade-card rounded-sm p-4 mb-6 flex items-center justify-between flex-wrap gap-3">
          <button
            onClick={toggleSelectAll}
            className="flex items-center gap-2 text-xs text-white/70 hover:text-white transition-colors"
            data-testid="select-all-btn"
          >
            {allSelected
              ? <CheckSquare size={16} className="trade-gold" />
              : <Square size={16} />
            }
            {allSelected ? "Deselect All" : "Select All"}
          </button>
          <div className="flex items-center gap-3">
            <span className="text-xs text-white/50" data-testid="selected-count">
              {selected.length} selected
            </span>
            <Button
              onClick={() => setComposeOpen(true)}
              disabled={selected.length === 0}
              className="trade-btn-primary rounded-sm h-9 px-4 text-xs"
              data-testid="compose-btn"
            >
              <Megaphone size={14} className="mr-2" /> Text / Advertise
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="text-center p-20 text-white/40">Loading...</div>
        ) : filtered.length === 0 ? (
          <div className="trade-card rounded-sm p-16 text-center">
            <UserCog className="w-16 h-16 trade-gold mx-auto mb-4 opacity-40" strokeWidth={1} />
            <h3 className="text-xl font-serif mb-2">No agents yet</h3>
            <p className="text-white/50 mb-6">Add transit and shipping agents to coordinate your imports</p>
            <Link to="/trade/agents/new">
              <Button className="trade-btn-primary rounded-sm">
                <Plus size={16} className="mr-2" /> Add Agent
              </Button>
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {filtered.map((a, i) => (
              <motion.div
                key={a.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.05 }}
              >
                <Link to={`/trade/agents/${a.id}`} className="group block trade-card rounded-sm p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <button
                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleSelect(a.id); }}
                        className="text-white/50 hover:text-[#D4AF37] transition-colors"
                        data-testid={`agent-checkbox-${a.id}`}
                        title="Select for bulk message"
                      >
                        {selected.includes(a.id)
                          ? <CheckSquare size={18} className="trade-gold" />
                          : <Square size={18} />
                        }
                      </button>
                      <div className={`w-12 h-12 rounded-sm flex items-center justify-center ${
                        ["transit","shipping"].includes(a.role) ? "bg-[#D4AF37]/10" : "bg-[#10b981]/10"
                      }`}>
                      {["transit","shipping"].includes(a.role)
                        ? <UserCog className={`w-6 h-6 ${a.role === "shipping" ? "trade-green" : "trade-gold"}`} strokeWidth={1.5} />
                        : <Truck className="w-6 h-6 trade-green" strokeWidth={1.5} />
                      }
                      </div>
                    </div>
                    <div className="flex gap-1">
                      {[...Array(5)].map((_, idx) => (
                        <Star key={idx} size={12} className={idx < (a.rating || 0) ? "fill-[#D4AF37] text-[#D4AF37]" : "text-white/20"} />
                      ))}
                    </div>
                  </div>
                  
                  <h3 className="font-serif font-semibold text-lg mb-1">{a.name}</h3>
                  <p className={`text-[10px] uppercase tracking-widest mb-2 ${
                    ["transit","shipping"].includes(a.role) ? "trade-gold" : "trade-green"
                  }`}>
                    {ROLE_LABELS[a.role] || a.role}
                  </p>
                  {a.company && <p className="text-xs text-white/60 mb-2">{a.company}</p>}
                  {(a.city || a.country) && (
                    <p className="text-xs text-white/50 flex items-center gap-1 mb-3">
                      <MapPin size={10} /> {[a.city, a.country].filter(Boolean).join(", ")}
                    </p>
                  )}
                  
                  {a.services && (
                    <p className="text-xs text-white/60 mb-3 line-clamp-2">{a.services}</p>
                  )}

                  <div className="space-y-2 pt-4 border-t border-white/5">
                    {a.contact_person && <p className="text-xs text-white/70">{a.contact_person}</p>}
                    {a.phone && (
                      <p className="text-xs text-white/50 flex items-center gap-2">
                        <Phone size={10} /> {a.phone}
                      </p>
                    )}
                    {a.whatsapp && (
                      <p className="text-xs text-white/50 flex items-center gap-2">
                        <span className="text-green-400">💬</span> {a.whatsapp}
                      </p>
                    )}
                    {a.wechat && (
                      <p className="text-xs text-white/50 flex items-center gap-2">
                        <MessageCircle size={10} /> {a.wechat}
                      </p>
                    )}
                  </div>

                  <div className="flex gap-2 mt-4 pt-4 border-t border-white/5">
                    {a.phone && (
                      <CallPrompt
                        phone={a.phone}
                        name={a.name}
                        className="flex-1 text-xs bg-white/5 text-white/70 hover:bg-white/10 px-3 py-2 rounded-sm flex items-center justify-center gap-1 transition-colors"
                        testid={`call-agent-${a.id}`}
                      />
                    )}
                    <ContactAudioRecordButton
                      phone={a.phone}
                      name={a.name}
                      className="flex-1 text-xs bg-[#D4AF37]/10 trade-gold hover:bg-[#D4AF37]/20 px-3 py-2 rounded-sm flex items-center justify-center gap-1 transition-colors"
                      testid={`audio-agent-${a.id}`}
                    />
                    {a.phone && (
                      <RecordedCallButton
                        phone={a.phone}
                        name={a.name}
                        contactId={a.id}
                        contactType="agent"
                        className="flex-1 text-xs bg-red-500/10 text-red-300 hover:bg-red-500/20 px-3 py-2 rounded-sm flex items-center justify-center gap-1 transition-colors"
                        testid={`record-call-agent-${a.id}`}
                      />
                    )}
                    {(a.whatsapp || a.phone) && (
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          window.open(`https://wa.me/${waNumber(a.whatsapp || a.phone)}`, "_blank", "noopener,noreferrer");
                        }}
                        className="flex-1 text-xs bg-[#10b981]/10 trade-green hover:bg-[#10b981]/20 px-3 py-2 rounded-sm flex items-center justify-center gap-1 transition-colors"
                        data-testid={`whatsapp-agent-${a.id}`}
                      >
                        <MessageCircle size={12} /> WhatsApp
                      </button>
                    )}
                    <button
                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); setAttachAgent(a); }}
                      className="flex-1 text-xs trade-gold hover:bg-[#D4AF37]/10 px-3 py-2 rounded-sm flex items-center justify-center gap-1 transition-colors"
                      data-testid={`attach-file-btn-${a.id}`}
                    >
                      <Paperclip size={12} /> Attach File
                    </button>
                    <button
                      onClick={(e) => handleDelete(a.id, e)}
                      className="text-xs text-red-400/60 hover:text-red-400 transition-colors flex items-center gap-1 ml-auto"
                    >
                      <Trash2 size={12} /> Delete
                    </button>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        )}
        {/* Bulk compose modal */}
        {composeOpen && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
            onClick={() => setComposeOpen(false)}
            data-testid="compose-modal"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              className="trade-card rounded-sm p-6 w-full max-w-lg max-h-[85vh] overflow-y-auto"
              onClick={e => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-5">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-[#D4AF37]/10 rounded-sm flex items-center justify-center">
                    <Megaphone className="w-5 h-5 trade-gold" strokeWidth={1.5} />
                  </div>
                  <div>
                    <h3 className="font-serif font-semibold text-lg">Message Agents</h3>
                    <p className="text-xs text-white/50">{selected.length} selected</p>
                  </div>
                </div>
                <button
                  onClick={() => setComposeOpen(false)}
                  className="p-2 text-white/40 hover:text-white transition-colors"
                  data-testid="compose-close-btn"
                >
                  <X size={18} />
                </button>
              </div>

              <div className="flex items-center justify-between mb-2">
                <p className="text-xs uppercase tracking-widest text-white/40">Your message</p>
                <button
                  onClick={copyMessage}
                  className="flex items-center gap-1 text-xs trade-gold hover:underline"
                  data-testid="compose-copy-btn"
                >
                  {copied ? <CheckSquare size={12} /> : <Copy size={12} />} {copied ? "Copied" : "Copy"}
                </button>
              </div>
              <Textarea
                value={message}
                onChange={e => setMessage(e.target.value)}
                rows={5}
                placeholder="Type your message once — then send to all selected agents or one by one"
                className="trade-input rounded-sm mb-5"
                data-testid="compose-message-input"
              />

              {selectedPhones.length > 0 && (
                <a
                  href={`sms:${selectedPhones.join(",")}?&body=${encodeURIComponent(message)}`}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 mb-5 text-sm rounded-sm trade-btn-primary"
                  data-testid="text-all-btn"
                >
                  <MessageSquare size={16} /> Text All {selectedPhones.length} in One Message
                </a>
              )}

              <p className="text-xs uppercase tracking-widest text-white/40 mb-3">Or send one by one</p>
              <div className="space-y-3">
                {selectedAgents.map(a => (
                  <div
                    key={a.id}
                    className="flex items-center justify-between gap-3 p-3 bg-white/5 rounded-sm"
                    data-testid={`compose-agent-${a.id}`}
                  >
                    <div className="min-w-0">
                      <p className="text-sm font-semibold truncate">{a.name}</p>
                      <p className="text-[11px] text-white/40 truncate">{ROLE_LABELS[a.role] || a.role}</p>
                    </div>
                    <div className="flex gap-2 flex-shrink-0">
                      {a.phone && (
                        <a
                          href={`sms:${a.phone}?&body=${encodeURIComponent(message)}`}
                          className="flex items-center gap-1.5 px-3 py-2 text-xs rounded-sm bg-[#D4AF37]/10 trade-gold hover:bg-[#D4AF37]/20 transition-colors"
                          data-testid={`compose-text-${a.id}`}
                        >
                          <MessageSquare size={12} /> Text
                        </a>
                      )}
                      {(a.whatsapp || a.phone) && (
                        <a
                          href={`https://wa.me/${waNumber(a.whatsapp || a.phone)}?text=${encodeURIComponent(message)}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-1.5 px-3 py-2 text-xs rounded-sm bg-[#10b981]/10 trade-green hover:bg-[#10b981]/20 transition-colors"
                          data-testid={`compose-whatsapp-${a.id}`}
                        >
                          <MessageCircle size={12} /> WhatsApp
                        </a>
                      )}
                      {a.email && (
                        <a
                          href={`mailto:${a.email}?subject=${encodeURIComponent("Goldspot Trade")}&body=${encodeURIComponent(message)}`}
                          className="flex items-center gap-1.5 px-3 py-2 text-xs rounded-sm bg-[#D4AF37]/10 trade-gold hover:bg-[#D4AF37]/20 transition-colors"
                          data-testid={`compose-email-${a.id}`}
                        >
                          <Mail size={12} /> Email
                        </a>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        )}
        {attachAgent && (
          <SendDocumentModal contact={attachAgent} onClose={() => setAttachAgent(null)} />
        )}
      </div>
    </div>
  );
};

export const AgentForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { API, authHeader } = useAuth();
  const isEdit = !!id && id !== "new";

  const [form, setForm] = useState({
    name: "", role: "transit", company: "", contact_person: "",
    phone: "", email: "", wechat: "", whatsapp: "", telegram: "",
    city: "", country: "", services: "", notes: "", rating: 5
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isEdit) {
      axios.get(`${API}/trade/agents`, { headers: authHeader() })
        .then(res => {
          const found = res.data.find(s => s.id === id);
          if (found) setForm(found);
        });
    }
  }, [id]);

  const handleSave = async () => {
    if (!form.name) {
      toast.error("Agent name is required");
      return;
    }
    setSaving(true);
    try {
      if (isEdit) {
        await axios.put(`${API}/trade/agents/${id}`, form, { headers: authHeader() });
        toast.success("Agent updated");
      } else {
        await axios.post(`${API}/trade/agents`, form, { headers: authHeader() });
        toast.success("Agent created");
      }
      navigate("/trade/agents");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="trade-section pt-32 lg:pt-20 pb-20 min-h-screen">
      <div className="lg:ml-64 px-6 md:px-12 py-10 max-w-3xl">
        <Link to="/trade/agents" className="inline-flex items-center gap-2 text-sm text-white/60 hover:trade-gold mb-6">
          <ArrowLeft size={16} /> Back
        </Link>
        <h1 className="text-3xl md:text-4xl font-serif font-bold mb-8">
          {isEdit ? "Edit" : "New"} Agent
        </h1>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="trade-card rounded-sm p-8 space-y-5"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs text-white/60">Agent Type *</Label>
              <Select value={form.role} onValueChange={v => setForm({...form, role: v})}>
                <SelectTrigger className="trade-input mt-1 h-11 rounded-sm" data-testid="agent-role-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#142039] border-white/10">
                  <SelectItem value="transit">China Transit Agent (supplier → shipper)</SelectItem>
                  <SelectItem value="shipping">Shipping Line Agent</SelectItem>
                  <SelectItem value="djibouti_transporter">Djibouti → Ethiopia Transporter</SelectItem>
                  <SelectItem value="ethiopia_transiter">Ethiopia Transiter (customs/clearance)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-white/60">Name *</Label>
              <Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="trade-input mt-1 h-11 rounded-sm" data-testid="agent-name-input" />
            </div>
            <div>
              <Label className="text-xs text-white/60">Company</Label>
              <Input value={form.company} onChange={e => setForm({...form, company: e.target.value})} className="trade-input mt-1 h-11 rounded-sm" />
            </div>
            <div>
              <Label className="text-xs text-white/60">Contact Person</Label>
              <Input value={form.contact_person} onChange={e => setForm({...form, contact_person: e.target.value})} className="trade-input mt-1 h-11 rounded-sm" />
            </div>
            <div>
              <Label className="text-xs text-white/60">City</Label>
              <Input value={form.city} onChange={e => setForm({...form, city: e.target.value})} className="trade-input mt-1 h-11 rounded-sm" />
            </div>
            <div>
              <Label className="text-xs text-white/60">Country</Label>
              <Input value={form.country} onChange={e => setForm({...form, country: e.target.value})} placeholder="China / Djibouti / Ethiopia" className="trade-input mt-1 h-11 rounded-sm" />
            </div>
            <div>
              <Label className="text-xs text-white/60">Phone</Label>
              <Input value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} className="trade-input mt-1 h-11 rounded-sm" />
            </div>
            <div>
              <Label className="text-xs text-white/60">WhatsApp</Label>
              <Input value={form.whatsapp} onChange={e => setForm({...form, whatsapp: e.target.value})} placeholder="+86 123 4567" className="trade-input mt-1 h-11 rounded-sm" data-testid="agent-whatsapp-input" />
            </div>
            <div>
              <Label className="text-xs text-white/60">WeChat ID</Label>
              <Input value={form.wechat} onChange={e => setForm({...form, wechat: e.target.value})} className="trade-input mt-1 h-11 rounded-sm" data-testid="agent-wechat-input" />
            </div>
            <div>
              <Label className="text-xs text-white/60">Telegram</Label>
              <Input value={form.telegram} onChange={e => setForm({...form, telegram: e.target.value})} placeholder="@username" className="trade-input mt-1 h-11 rounded-sm" />
            </div>
            <div className="md:col-span-2">
              <Label className="text-xs text-white/60">Email</Label>
              <Input value={form.email} onChange={e => setForm({...form, email: e.target.value})} className="trade-input mt-1 h-11 rounded-sm" />
            </div>
            <div className="md:col-span-2">
              <Label className="text-xs text-white/60">Services Offered</Label>
              <Input value={form.services} onChange={e => setForm({...form, services: e.target.value})} placeholder="e.g. Customs clearance, consolidation, trucking" className="trade-input mt-1 h-11 rounded-sm" />
            </div>
            <div className="md:col-span-2">
              <Label className="text-xs text-white/60">Rating</Label>
              <div className="flex gap-2 mt-2">
                {[1,2,3,4,5].map(n => (
                  <button
                    key={n}
                    type="button"
                    onClick={() => setForm({...form, rating: n})}
                    className="transition-transform hover:scale-110"
                  >
                    <Star size={24} className={n <= form.rating ? "fill-[#D4AF37] text-[#D4AF37]" : "text-white/20"} />
                  </button>
                ))}
              </div>
            </div>
            <div className="md:col-span-2">
              <Label className="text-xs text-white/60">Notes</Label>
              <Textarea value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} rows={4} className="trade-input mt-1 rounded-sm" />
            </div>
          </div>

          <Button onClick={handleSave} disabled={saving} className="w-full trade-btn-primary rounded-sm h-12" data-testid="save-agent-btn">
            <Save size={16} className="mr-2" />
            {saving ? "Saving..." : (isEdit ? "Update" : "Create") + " Agent"}
          </Button>
        </motion.div>
      </div>
    </div>
  );
};
